import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'sonner';

const GymContext = createContext(null);

export const GymProvider = ({ children }) => {
  const [gyms, setGyms] = useState([]);
  const [selectedGym, setSelectedGym] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const loadGyms = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/gyms');
      const gymsList = response.data;
      setGyms(gymsList);
      
      // Select default gym or first gym
      const defaultGym = gymsList.find(g => g.is_default) || gymsList[0];
      if (defaultGym) {
        const savedGymId = localStorage.getItem('selectedGymId');
        const gymToSelect = gymsList.find(g => g.id === savedGymId) || defaultGym;
        setSelectedGym(gymToSelect);
      }
      setInitialized(true);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGyms();
    
    // Listen for token changes
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          loadGyms();
        } else {
          setGyms([]);
          setSelectedGym(null);
          setInitialized(false);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const selectGym = (gym) => {
    setSelectedGym(gym);
    localStorage.setItem('selectedGymId', gym.id);
  };

  const addGym = async (gymData) => {
    try {
      const response = await api.post('/gyms', gymData);
      const newGym = response.data;
      setGyms([...gyms, newGym]);
      toast.success('Academia adicionada com sucesso!');
      return newGym;
    } catch (error) {
      toast.error('Erro ao adicionar academia');
      throw error;
    }
  };

  const updateGym = async (gymId, gymData) => {
    try {
      const response = await api.put(`/gyms/${gymId}`, gymData);
      const updatedGym = response.data;
      setGyms(gyms.map(g => g.id === gymId ? updatedGym : g));
      if (selectedGym?.id === gymId) {
        setSelectedGym(updatedGym);
      }
      toast.success('Academia atualizada com sucesso!');
      return updatedGym;
    } catch (error) {
      toast.error('Erro ao atualizar academia');
      throw error;
    }
  };

  const deleteGym = async (gymId) => {
    try {
      await api.delete(`/gyms/${gymId}`);
      const updatedGyms = gyms.filter(g => g.id !== gymId);
      setGyms(updatedGyms);
      
      if (selectedGym?.id === gymId) {
        const defaultGym = updatedGyms.find(g => g.is_default) || updatedGyms[0];
        selectGym(defaultGym);
      }
      
      toast.success('Academia deletada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar academia');
      throw error;
    }
  };

  return (
    <GymContext.Provider value={{
      gyms,
      selectedGym,
      loading,
      selectGym,
      addGym,
      updateGym,
      deleteGym,
      refreshGyms: loadGyms
    }}>
      {children}
    </GymContext.Provider>
  );
};

export const useGym = () => {
  const context = useContext(GymContext);
  if (!context) {
    throw new Error('useGym must be used within GymProvider');
  }
  return context;
};
